
import { takeUntil } from 'rxjs/operators';
import { Component, OnInit, OnChanges, Input, SimpleChanges, HostBinding } from '@angular/core';
import { NavigationService } from '../common';
import { Location } from '@angular/common';
import { UserProfileBookmarkService } from '../common/services/user-profile-bookmark.service';
import { BaseComponentOnDestroy } from '../common/epics/base-component-on-destroy';
import { Router, NavigationEnd } from '@angular/router';
import { GoogleAnalyticsService } from '../common/services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-bookmark-side-menu',
  templateUrl: './bookmark-side-menu.component.html',
  styleUrls: ['./bookmark-side-menu.component.less']
})
export class BookmarkSideMenuComponent extends BaseComponentOnDestroy implements OnInit, OnChanges {

  readonly TITLE = '-title';
  readonly DATE  = '-creationDate';
  readonly ICON  = '-icon';

  localization = {
    noBookmarksMessage          :   'You have no bookmarks',
    editBookmarkTitleLabel      :   'Edit Bookmark Title',
    updateBookmarkTitleLabel    :   'Update Bookmark Title',
    deleteBookmarkLabel         :   'Delete Bookmark',
    titleLabel                  :   'Title',
    dateLabel                   :   'Date',
    typeLabel                   :   'Type'
  };

  component = {
    items: [],
    itemsGrouped: [],
    count: 0,
  };
  isUpdateBookmarkFormList = [];

  bookmarkTitle = '';
  url = ''; // url of the current page
  bookmarked = false;
  showAdd = false;
  showAddingBookmark = true;
  showEditAndDelete = false;
  showOrderOptions = false;
  firstActiveNoLonger = true;
  orderByRepeatProperty = '';
  orderByRepeatReverse = true;

  @Input() eventName;
  // Host binding used to ensure custom css only effects the scope of this component
  @HostBinding('class.BookmarkSideMenuComponent') BookmarkSideMenuComponent: boolean = true;

  constructor(
    private navigationService: NavigationService,
    private locationService: Location,
    private bookmarkService: UserProfileBookmarkService,
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    super();
  }

  ngOnInit() {
    this.getBookmarks();
    this.router.events.pipe(takeUntil(this.isDestroyed$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.bookmarked = this.isUrlBookmarked();
        this.changeFavouriteIconOutsideScope();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.eventName) {
      if (this.eventName === 'AddToFavourites' || this.eventName === 'AddToFavouritesA') {
        this.showAdd = true;
        this.showEditAndDelete = false;
        this.showOrderOptions = false;
        this.bookmarkTitle = this.showAddPreparation();
      } else { // edit or list
        if (this.component.items.length > 0) {
          this.showAdd = false;
          this.showEditAndDelete = false;
          this.showOrderOptions = false;
        } else { // no bookmarks need to add one
          this.showAdd = true;
          this.bookmarkTitle = this.showAddPreparation();
        }
      }
    }

  }

  /**
   * Prepare a name for the new shortcut the user can add quickly
   */
  showAddPreparation() {
    let prependTitle = '';
    const titleFeed = this.navigationService.getPageTitleDecorator();
    if (titleFeed && titleFeed.length > 0) {
      prependTitle = titleFeed[titleFeed.length - 1].Text ?? '';
      if (titleFeed.length > 1) {
        if (!isNaN(+prependTitle) || !isNaN(parseInt(prependTitle.charAt(0), 10))) {
          // If first character is a number
          prependTitle = titleFeed[titleFeed.length - 2].Text.trim() + ' - ' + prependTitle;
        }
      }
    }
    return prependTitle;
  }

  /**
   * Order the book mark items. Defaults to order by TITLE if provided parameter isnt valid.
   * @param orderBy Field to order by
   */
  sort(orderBy: string) {
    if (this.orderByRepeatProperty === orderBy) {
      this.orderByRepeatReverse = !this.orderByRepeatReverse;
      this.component.items.reverse();
    } else {
      this.orderByRepeatProperty = orderBy;
      if (orderBy === this.ICON) {
        this.sortByType();
      } else if (orderBy === this.DATE) {
        this.sortByCreateDate();
      } else { // default to sort by title
        this.sortByTitle();
      }
    }

  }

  /**
   * Sort the component items by title
   */
  sortByTitle() {
    this.component.items.sort(({title: a}, {title: b}) => {
      const titleA = a.toLowerCase();
      const titleB = b.toLowerCase();
      // sort string ascending
      if (titleA < titleB) {
        return -1;
      }
      if (titleA > titleB) {
        return 1;
      }
      return 0; // default return value (no sorting)
    });
  }

  /**
   * Sort the component items by creation date
   */
  sortByCreateDate() {
    this.component.items.sort(({creationDate: a}, {creationDate: b}) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Sort the component items by type/icon
   */
  sortByType() {
    this.component.items.sort(({icon: a}, {icon: b}) => {
      const iconA = a.toLowerCase();
      const iconB = b.toLowerCase();
      if (iconA < iconB) {
        return -1;
      }
      if (iconA > iconB) {
        return 1;
      }
      return 0;
    });
  }

  bookmarkAdd() {
    const associatedType = this.router.url.split('/')[2]; // value after 'next' in route

    // // Check to make sure that the route name doesn't already exist
    const foundPathName = this.isUrlBookmarked();

    const titleFeed = this.navigationService.getPageTitleDecorator();
    let setIcon = '';
    if (titleFeed && titleFeed.length > 0) {
      setIcon = titleFeed[0].Icon;
    }

    // // Add the Statename to be able to href to it, into component.items
    if (!foundPathName && this.bookmarkTitle && associatedType) {
      // Make subfolders, split State name at dots, base parent/folder upon first name before dot; stateRoutingName
      const newBookmarkObj = {
        WorkflowPendingTaskId: -1,
        pathName: this.router.url,
        statePath: '',
        description: 'No description given.',
        title: this.bookmarkTitle,
        creationDate: new Date(),
        icon: setIcon,
        associatedType
      };
      this.component.items.push(newBookmarkObj);
      this.bookmarkService.addUpdateBookmark(newBookmarkObj).then(() => { this.getBookmarks(); });
      this.bookmarked = true;
      this.changeFavouriteIconOutsideScope();
      this.showAddingBookmark = false;

      this.sendBookmarkGaEvent('add', newBookmarkObj);

      // Add bookmark to grouped list on the fly
      let noFolder = true;
      for (const item of this.component.itemsGrouped) {
        if (item.folderType === associatedType) {
          noFolder = false;
          item.subItems.push(newBookmarkObj);
          break;
        }
      }

      // Create a new folder if none fits the existing criteria
      if (noFolder) {
        const folderName = associatedType.toUpperCase().trim().replace(/\s/, '');
        const subFolder = [];
        subFolder.push(newBookmarkObj);
        const newfolderObj = {
          folderName,
          folderType: associatedType,
          subItems: subFolder,
          icon: 'business_center', // not sure what icon this was pointing too
          hasSubItems: true,
          code: 'BookmarkFolder' + associatedType
        };

        // Add folder object to itemsGrouped
        this.component.itemsGrouped.push(newfolderObj);
      }
    }

    this.showAdd = false;
  }

  getBookmarks() {
    // request bookmark settings, using the user id, which is found in the assembler
    const that = this; // this inside was undefined for some reason on page refresh, work around with that
    this.bookmarkService.getBookmarkByUser().then((response) => {
      that.component.items = [];
      if (response.Items && response.Items.length > 0) {

        for (const item of response.Items) {
          const bookmark = {
            id: item.Id,
            lastModifiedDatetime: item.LastModifiedDatetime,
            pathName: item.PathName,
            statePath: item.StatePath,
            description: item.Description,
            creationDate: item.CreationDate,
            title: item.Title,
            icon: item.Icon,
            associatedType: item.AssociatedType,
            tempValue: item.Title,
            showEditDeleteSaveMenu: false
          };
          that.component.items.push(bookmark);
          // location service for on refresh of page, that.url hasnt resolved intime on page refresh
          if (that.url === item.PathName || that.locationService.path() === item.PathName) {
            that.bookmarked = true;
          }
        }

        if (that.bookmarked) {
          that.showAdd = false;
          that.changeFavouriteIconOutsideScope();
        } else {
          that.showAdd = true;
        }
        // that.component.items = response.Items;
        that.component.count = response.Items.length;

        // Make a new list that has the bookmarks grouped accordingly, and can be grouped in folders
        // scope.makeGroupedBookmarkList();
      }
    }, (err) => {
      console.error('getBookmarkByUser', err);
    });
  }

  bookmarkUpdate(item) {
    const title = item.tempValue;
    const description = 'No description given.';
    const indexOfBookmark = this.component.items.findIndex(i => i.pathName === item.pathName);
    const foundPathName = indexOfBookmark > -1;

    if (foundPathName && title) {
      this.component.items[indexOfBookmark].description = description;
      this.component.items[indexOfBookmark].title = title;
      const command = this.component.items[indexOfBookmark];
      command.WorkflowPendingTaskId = -1;
      this.bookmarkService.addUpdateBookmark(command).then(() => { this.getBookmarks(); });
      // close update screen
      const updateBookmarkIndex = this.isUpdateBookmarkFormList.indexOf(item.pathName);
      if (updateBookmarkIndex > -1) {
        this.isUpdateBookmarkFormList.splice(updateBookmarkIndex, 1);
      }
    }
  }

  updateBookmarkPrecursor(item) {
    const isFound = this.isUpdateBookmarkFormList.includes(item.pathName);
    if (!isFound) {
      this.isUpdateBookmarkFormList.push(item.pathName);
    }
  }

  bookmarkDelete(item) {
    // component.items and the source of this array, needs to remove the currently selected bookmark.
    // Hence, replace the current BookmarkList on the server side

    const indexOfBookmark = this.component.items.findIndex(i => i.pathName === item.pathName);
    const foundPathName = indexOfBookmark > -1 ? true : false;

    // close update screen
    const updateBookmarkIndex = this.isUpdateBookmarkFormList.indexOf(item.pathName);
    if (updateBookmarkIndex > -1) {
      this.isUpdateBookmarkFormList.splice(updateBookmarkIndex, 1);
    }

    if (foundPathName) {
      this.component.items = this.component.items.filter(i => i.pathName !== item.pathName);
      // Cut item out of component.items
      const objTemp = {
        Id: item.id,
        LastModifiedDatetime: item.lastModifiedDatetime,
        pathName: item.pathName
      };
      
      this.bookmarkService.bookmarkDelete(objTemp).then(() => {

        if (item.pathName === this.router.url) {
          this.bookmarked = false;
          this.changeFavouriteIconOutsideScope();
          this.showAdd = false;
        }

        this.sendBookmarkGaEvent('delete', item);

        let subItemsLength = 0;
        for (let j = 0; j < this.component.itemsGrouped.length; j++) {
          subItemsLength = this.component.itemsGrouped[j].subItems.length;
          for (let k = 0; k < subItemsLength; k++) {
            if (this.component.itemsGrouped[j].subItems[k].pathName === item.pathName) {
              // get rid of folder
              if (this.component.itemsGrouped[j].subItems.length === 1) {
                if (this.component.itemsGrouped.length === 1) {
                  // There are no folders
                  this.component.itemsGrouped = [];
                } else {
                  // Remove one of the folder
                  this.component.itemsGrouped.splice(j, 1);
                }
              } else {
                this.component.itemsGrouped[j].subItems.splice(k, 1);
              }
              break;
            }
          }
        }
      });
    }
  }

  // not used, brought from old code
  makeGroupedBookmarkList() {
    // Idea: scan _component.items and for each unique first part of a StateName(prior to reaching a dot in the State.name),
    // make a new object if it doesn't already exist and shove the bookmark inside.
    this.component.itemsGrouped.length = 0;
    for (const componentItem of this.component.items) {
      const associatedType = componentItem.associatedType;
      let foundFoldername = false;
      for (const groupedItem of this.component.itemsGrouped) {
        if (groupedItem.folderType === associatedType) {
          foundFoldername = true;
          groupedItem.subItems.push(componentItem);
        }
      }

      if (!foundFoldername) {
        // Create a new folder object,
        const subFolder = [];
        // The folder names are all lower case, therefore make the first letter a capital
        const folderName = associatedType.replace(associatedType[0], associatedType[0].toUpperCase());
        subFolder.push(componentItem);
        const newfolderObj = {
          folderName,
          folderType: associatedType,
          subItems: subFolder,
          icon: componentItem.icon,
          hasSubItems: true,
          code: 'BookmarkFolder' + associatedType
        };

        // Add folder object to itemsGrouped
        this.component.itemsGrouped.push(newfolderObj);
      }
    }
  }

  isFoundPathNameInUpdateList(item) {
    return this.isUpdateBookmarkFormList.includes(item.pathName);
  }

  /**
   * Change the star icon inside of sidenav component
   */
  changeFavouriteIconOutsideScope() {
    // search by class 'shortcut-add-bookmark', and replace the innerhtml from 'star_border' to 'star'
    const shortcutAddBookmark = document.getElementsByClassName('shortcut-add-bookmark');
    const newVal = this.bookmarked ? 'star' : 'star_border';
    if (newVal !== shortcutAddBookmark[0].innerHTML.trim()) { // only change innerHTML when value is different
      shortcutAddBookmark[0].innerHTML = newVal;
    }
  }

  isUrlBookmarked(): boolean {
    return this.component.items.some(i => i.pathName === this.router.url);
  }

  /**
   * Redirect page to url
   * @param url Page to navigate to
   */
  redirection(url) {
    const bookmark = this.component.items.find(f => f?.pathName === url);
    if (bookmark) {
      this.sendBookmarkGaEvent('navigate', bookmark);
    }

    this.router.navigateByUrl(url);
  }

  private sendBookmarkGaEvent(action: string, bookmark: { pathName: string, title: string; }) {
    this.googleAnalyticsService.sendClickData({
      feature: 'Bookmarks',
      action,
      type: `${bookmark?.pathName || 'No URL'} - ${bookmark?.title || 'No title'}`
    });
  }
}
